import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'antd';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Rocket } from 'lucide-react';
import { notification } from 'antd';
import WelcomeStage from './stages/WelcomeStage';
import BusinessInfoReviewStage from './stages/BusinessInfoReviewStage';
import CallTransferWorkflowStage from './stages/CallTransferWorkflowStage';
import TextingWorkflowStage from './stages/TextingWorkflowStage';
import IntakeFormWorkflowStage from './stages/IntakeFormWorkflowStage';
import GoLiveStage from './stages/GoLiveStage';
import { getSelectedAgent, getSelectedAgentId } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateAgentKnowledgeBaseAndWorkflows } from '@/api/user';
import posthog from 'posthog-js';
import { AppRoutes } from 'src/enums/ERoutes';
import { LocalStorageKeys } from 'src/enums/ELocalStorage';
import LocalStorageUtility from '@/utils/localStorage';
import { safeJSONParse } from '@/utils/helper';

interface OnboardingModalProps {
	visible: boolean;
	onComplete: () => void;
	debrand?: boolean;
	handleClose?: () => void;
}

interface OnboardingStage {
	id: number;
	component: React.ReactNode;
	name?: 'callWorkflows' | 'textingWorkflows' | 'intakeWorkflows';
}

// Interfaces for onboarding data
interface BusinessHours {
	[key: string]: { open: string; close: string; closed: boolean };
}

interface PricingItem {
	id: string;
	name: string;
	price: string;
	description?: string;
}

interface FAQ {
	id: string;
	question: string;
	answer: string;
}

interface AddressItem {
	id: string;
	locationType: string;
	address: string;
}

interface CallWorkflow {
	id: string;
	description: string;
	workflowForwardingNumber: string;
	workflowForwardingDID?: string;
	backupNumbers: string[];
	voiceResponse: string;
	whisperType: string;
	handoffMessageType: string;
	handoffMessage: string;
	callTransferee: string;
	displayedNumber: string;
}

interface TextingWorkflow {
	id: string;
	description: string;
	textMessage: string;
	shortURLEnabled: boolean;
	askForPermissionOnText: boolean;
	postExecutionPrompt: string;
}

interface IntakeWorkflow {
	id: string;
	description: string;
	customScenario: string;
	questions: string[];
	postExecutionPrompt?: string;
	intakeBeginningNatural?: boolean;
}

interface StructuredKnowledgeBaseData {
	additionalInformation?: string;
	address?: Record<string, string>;
	businessName?: string;
	hours?: Record<string, string>;
	phone?: string;
	pricing?: Record<string, string>;
	faqs?: Record<string, string>;
}

interface OnboardingData {
	// Stage 1 - Welcome data (if needed in future)
	welcomeCompleted: boolean;

	// Stage 2 - Business Info Review
	businessName: string;
	phoneNumber: string;
	addresses: AddressItem[];
	businessHours: BusinessHours;
	pricing: PricingItem[];
	faqs: FAQ[];
	additionalInfo: string;

	// Stage 3 - Call Transfer Workflows
	callWorkflows: CallWorkflow[];

	// Stage 4 - Texting Workflows
	textingWorkflows: TextingWorkflow[];

	// Stage 5 - Intake Form Workflows
	intakeWorkflows: IntakeWorkflow[];
}

interface OnboardingMutationData {
	agentId: string;
	structuredData: {
		businessName?: string;
		phoneNumber?: string;
		addresses?: AddressItem[];
		businessHours?: BusinessHours;
		pricing?: PricingItem[];
		faqs?: FAQ[];
		additionalInfo?: string;
	};
	workflows: {
		callWorkflows?: CallWorkflow[];
		intakeWorkflows?: IntakeWorkflow[];
		textingWorkflows?: TextingWorkflow[];
	};
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({ visible, onComplete, handleClose }) => {
	const [currentStage, setCurrentStage] = useState(0);
	const selectedAgent = useAppSelector(getSelectedAgent);
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const isResellerDashboard = location.pathname.includes(AppRoutes.RESELLER_PORTAL);

	const queryClient = useQueryClient();

	const onboardingMutation = useMutation({
		mutationKey: ['updateKnowledgeBaseAndWorkflows'],
		mutationFn: ({ agentId, structuredData, workflows }: OnboardingMutationData) => {
			// Transform intake workflows into the correct API format
			const customIntakeWorkflows =
				workflows.intakeWorkflows?.filter((w) => w.description === 'custom-scenario') || [];

			const transformedData = {
				structuredData,
				workflows: {
					callWorkflows: workflows.callWorkflows || [],
					// Transform intake workflows to match API expectations
					intake_questions_workflows:
						customIntakeWorkflows.length > 0 ? JSON.stringify(customIntakeWorkflows) : null,
					// Transform texting workflows to JSON string
					workflows: JSON.stringify(workflows.textingWorkflows || []),
				},
			};

			return updateAgentKnowledgeBaseAndWorkflows(agentId, transformedData);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['receptionist'] });
		},
		onError: (error: any) => {
			notification.error({
				message: 'Update Failed',
				description:
					error?.response?.data?.message ||
					'Failed to update knowledge base and workflows. Please try again.',
				duration: 5,
			});
		},
	});

	// Initialize default onboarding data
	const getDefaultOnboardingData = (agent?: any): OnboardingData => {
		// Parse call workflows with fallback
		const callWorkflows = Array.isArray(agent?.callWorkflows) ? agent.callWorkflows : [];

		// Parse intake questions and workflows
		const intakeWorkflows: IntakeWorkflow[] = [];

		// Custom scenario intake workflows
		if (agent?.intake_questions_workflows) {
			try {
				const customWorkflows = safeJSONParse(agent.intake_questions_workflows, []);
				if (Array.isArray(customWorkflows)) {
					customWorkflows.forEach((workflow, index) => {
						if (workflow && typeof workflow === 'object') {
							intakeWorkflows.push({
								id: workflow.id || `custom-workflow-${index}`,
								description: 'custom-scenario',
								customScenario: workflow.customScenario || '',
								questions: Array.isArray(workflow.questions) ? workflow.questions : [],
								postExecutionPrompt: workflow.postExecutionPrompt || '',
							});
						}
					});
				}
			} catch (error) {
				console.error('Type mismatch in intake workflows', error);
			}
		}

		// Parse texting workflows
		let textingWorkflows: TextingWorkflow[] = [];
		if (agent?.workflows) {
			const workflows = safeJSONParse(agent.workflows, []);
			if (Array.isArray(workflows)) {
				textingWorkflows = workflows
					.filter((workflow) => workflow && typeof workflow === 'object')
					.map((workflow, index) => ({
						id: workflow.id || `texting-workflow-${index}`,
						description: workflow.description || '',
						textMessage: workflow.textMessage || '',
						shortURLEnabled: workflow.shortURLEnabled ?? true,
						askForPermissionOnText: workflow.askForPermissionOnText ?? false,
						postExecutionPrompt: workflow.postExecutionPrompt || '',
					}));
			}
		}

		const knowledgeBaseItems =
			typeof agent?.knowledgeBase === 'string'
				? safeJSONParse(agent?.knowledgeBase, [])
				: agent?.knowledgeBase;
		const segregatedKnowledgeBaseEntry = Array.isArray(knowledgeBaseItems)
			? knowledgeBaseItems.find((item) => 'structuredData' in item)?.structuredData
			: knowledgeBaseItems;
		const typeSafeStructuredData: StructuredKnowledgeBaseData =
			typeof segregatedKnowledgeBaseEntry === 'object' &&
			segregatedKnowledgeBaseEntry !== null &&
			Object.entries(segregatedKnowledgeBaseEntry)?.length > 0
				? segregatedKnowledgeBaseEntry
				: {};

		// Helper function to convert address object to AddressItem array
		const convertAddressToAddressItems = (address?: Record<string, string>): AddressItem[] => {
			if (!address || Object.keys(address).length === 0) {
				return [];
			}

			return Object.entries(address).map(([locationType, address], index) => ({
				id: (index + 1).toString(),
				locationType,
				address,
			}));
		};

		// Helper function to convert hours object to BusinessHours format
		const convertHoursToBusinessHours = (hours?: Record<string, string>): BusinessHours => {
			const defaultHours = {
				Monday: { open: '9:00 AM', close: '5:00 PM', closed: false },
				Tuesday: { open: '9:00 AM', close: '5:00 PM', closed: false },
				Wednesday: { open: '9:00 AM', close: '5:00 PM', closed: false },
				Thursday: { open: '9:00 AM', close: '5:00 PM', closed: false },
				Friday: { open: '9:00 AM', close: '5:00 PM', closed: false },
				Saturday: { open: '', close: '', closed: true },
				Sunday: { open: '', close: '', closed: true },
			};

			if (!hours) {
				return defaultHours;
			}

			const businessHours: BusinessHours = {};

			Object.keys(defaultHours).forEach((day) => {
				const dayLower = day.toLowerCase();
				const dayHours = hours[dayLower];

				if (dayHours && dayHours.toLowerCase() !== 'closed') {
					const [open, close] = dayHours.split(' - ');
					businessHours[day] = {
						open: open?.trim() || '',
						close: close?.trim() || '',
						closed: false,
					};
				} else {
					businessHours[day] = {
						open: '',
						close: '',
						closed: true,
					};
				}
			});

			return businessHours;
		};

		// Helper function to convert pricing object to PricingItem array
		const convertPricingToPricingItems = (pricing?: Record<string, string>): PricingItem[] => {
			if (!pricing || Object.keys(pricing).length === 0) {
				return [];
			}

			return Object.entries(pricing).map(([name, price], index) => ({
				id: (index + 1).toString(),
				name,
				price,
			}));
		};

		// Helper function to convert FAQs object to FAQ array
		const convertFaqsToFaqItems = (faqs?: Record<string, string>): FAQ[] => {
			if (!faqs || Object.keys(faqs).length === 0) {
				return [];
			}

			return Object.entries(faqs).map(([question, answer], index) => ({
				id: (index + 1).toString(),
				question,
				answer,
			}));
		};

		return {
			welcomeCompleted: false,
			businessName: typeSafeStructuredData?.businessName || '',
			phoneNumber:
				typeof typeSafeStructuredData?.phone === 'string' ? typeSafeStructuredData.phone : '',
			addresses: convertAddressToAddressItems(typeSafeStructuredData?.address),
			businessHours: convertHoursToBusinessHours(typeSafeStructuredData?.hours),
			pricing: convertPricingToPricingItems(typeSafeStructuredData?.pricing),
			faqs: convertFaqsToFaqItems(typeSafeStructuredData?.faqs),
			additionalInfo: typeSafeStructuredData?.additionalInformation || '',
			callWorkflows,
			textingWorkflows,
			intakeWorkflows,
		};
	};

	const [onboardingData, setOnboardingData] = useState<OnboardingData>(() =>
		getDefaultOnboardingData(selectedAgent),
	);

	const clearSpecificWorkflow = (
		workflowType: 'callWorkflows' | 'textingWorkflows' | 'intakeWorkflows',
	) => {
		switch (workflowType) {
			case 'callWorkflows':
				setOnboardingData((prev) => ({ ...prev, callWorkflows: [] }));
				break;
			case 'textingWorkflows':
				setOnboardingData((prev) => ({ ...prev, textingWorkflows: [] }));
				break;
			case 'intakeWorkflows':
				setOnboardingData((prev) => ({ ...prev, intakeWorkflows: [] }));
				break;
		}
	};

	// Load data from localStorage on mount
	useEffect(() => {
		if (visible) {
			try {
				const savedData = LocalStorageUtility.getLocalData<OnboardingData>(
					LocalStorageKeys.ONBOARDING_STORAGE_KEY,
				);
				if (savedData) {
					setOnboardingData(savedData);
				}
			} catch (error) {
				console.error('Error parsing saved onboarding data:', error);
				// If parsing fails, use agent data as fallback
				setOnboardingData(getDefaultOnboardingData(selectedAgent));
			}
		} else {
			// No saved data, use agent data
			setOnboardingData(getDefaultOnboardingData(selectedAgent));
		}
	}, [visible, selectedAgent]);

	// Save data to localStorage whenever onboardingData changes
	useEffect(() => {
		if (visible && onboardingData) {
			LocalStorageUtility.setLocalData(LocalStorageKeys.ONBOARDING_STORAGE_KEY, onboardingData);
		}
	}, [onboardingData, visible]);

	// Function to update onboarding data
	const updateOnboardingData = (updates: Partial<OnboardingData>) => {
		setOnboardingData((prev) => ({ ...prev, ...updates }));
	};

	// Check if current stage has meaningful changes
	const hasStageChanges = (stage: number): boolean => {
		switch (stage) {
			case 2: // Call Transfer Workflows
				return !!(
					onboardingData.callWorkflows?.length > 0 &&
					onboardingData.callWorkflows.some(
						(w) => w.description.trim() || w.workflowForwardingNumber.trim(),
					)
				);
			case 3: // Texting Workflows
				return !!(
					onboardingData.textingWorkflows?.length > 0 &&
					onboardingData.textingWorkflows.some(
						(w) => w.description.trim() || w.textMessage.trim() || w.postExecutionPrompt.trim(),
					)
				);
			case 4: // Intake Form Workflows
				return !!(
					onboardingData.intakeWorkflows?.length > 0 &&
					onboardingData.intakeWorkflows.some(
						(w) =>
							w.customScenario.trim() ||
							w.postExecutionPrompt?.trim() ||
							w.questions.some((q) => q.trim()),
					)
				);
			default:
				return false;
		}
	};

	// Get button text based on stage and changes
	const getNextButtonText = (): string => {
		if (currentStage === 0) {
			return 'Review my Receptionist';
		}
		if (currentStage === stages.length - 1) {
			return 'Call my Receptionist!';
		}

		// Business Info Review (stage 1) always shows "Next"
		if (currentStage === 1) {
			return 'Next';
		}

		const hasChanges = hasStageChanges(currentStage);
		if ([2, 3, 4].includes(currentStage) && !hasChanges) {
			return 'Skip & Continue';
		}
		return 'Next';
	};

	// Clear onboarding data when modal completes
	const handleComplete = () => {
		posthog.capture('completed-onboarding-v5');

		LocalStorageUtility.removeLocalData(LocalStorageKeys.ONBOARDING_STORAGE_KEY);
		LocalStorageUtility.removeLocalData(LocalStorageKeys.HAS_CLAIMED_FREE_CALL_KEY);

		setOnboardingData(getDefaultOnboardingData(selectedAgent));
		onComplete();
	};

	const handleNext = () => {
		const hasChanges = hasStageChanges(currentStage);

		// Business Info Review (stage 1) always requires validation
		// Other stages only validate if there are changes
		if (currentStage === 1 || hasChanges) {
			// Trigger validation in child components
			window.dispatchEvent(new Event('trigger-validation'));

			// Validation logic
			if (currentStage === 1) {
				// Business Info Review - validate required fields
				if (!onboardingData.businessName.trim()) {
					notification.error({
						message: 'Validation Error',
						description: 'Business name is required.',
					});
					return;
				}
				if (!onboardingData.phoneNumber.trim()) {
					notification.error({
						message: 'Validation Error',
						description: 'Phone number is required.',
					});
					return;
				}
				if (
					!onboardingData.addresses.length ||
					!onboardingData.addresses.some((addr) => addr.address.trim())
				) {
					notification.error({
						message: 'Validation Error',
						description: 'At least one address is required.',
					});
					return;
				}
			} else if (currentStage === 2) {
				// Call Transfer Workflows
				const callWorkflows = onboardingData.callWorkflows || [];
				for (const workflow of callWorkflows) {
					if (!workflow.description.trim() || !workflow.workflowForwardingNumber.trim()) {
						notification.error({
							message: 'Validation Error',
							description:
								'Please fill in all required fields for call transfer workflows (Scenario and Primary Transfer Number).',
						});
						return;
					}
				}
			} else if (currentStage === 3) {
				// Texting Workflows
				const textingWorkflows = onboardingData.textingWorkflows || [];
				for (const workflow of textingWorkflows) {
					if (
						!workflow.description.trim() ||
						!workflow.textMessage.trim() ||
						!workflow.postExecutionPrompt.trim() ||
						workflow.shortURLEnabled === undefined ||
						workflow.askForPermissionOnText === undefined
					) {
						notification.error({
							message: 'Validation Error',
							description:
								'Please fill in all required fields for texting workflows (Scenario, Message, Post-execution prompt, and toggle settings).',
						});
						return;
					}
				}
			} else if (currentStage === 4) {
				// Intake Form Workflows
				const intakeWorkflows = onboardingData.intakeWorkflows || [];
				for (const workflow of intakeWorkflows) {
					// Custom scenario validation
					if (workflow.description === 'custom-scenario') {
						if (
							!workflow.customScenario.trim() ||
							!workflow.postExecutionPrompt?.trim() ||
							!workflow.questions.some((q) => q.trim() !== '')
						) {
							notification.error({
								message: 'Validation Error',
								description:
									'Please fill in all required fields for custom scenario intake workflows (Customer Scenario, Post-execution prompt, and at least one question).',
							});
							return;
						}
					}
				}
			}
		} else {
			// No changes made, clear the workflow data for this stage (skip behavior)
			// Only applies to workflow stages (2, 3, 4), not Business Info Review (1)
			if (stages[currentStage].name) {
				clearSpecificWorkflow(stages[currentStage].name);
			}
		}

		// Track completion
		switch (currentStage) {
			case 1:
				posthog.capture('completed-business-info-review-v5');
				break;
			case 2:
				posthog.capture('completed-call-transfer-workflows-v5');
				break;
			case 3:
				posthog.capture('completed-texting-workflows-v5');
				break;
			case 4:
				posthog.capture('completed-intake-form-workflows-v5');
				break;
		}

		if (currentStage < stages.length - 1) {
			setCurrentStage(currentStage + 1);
		} else {
			// Final stage - submit data
			handleSubmitOnboarding();
		}
	};

	const handleSubmitOnboarding = async () => {
		if (!selectedAgentId) {
			notification.error({
				message: 'Error',
				description: 'Agent ID not found. Please try again.',
			});
			return;
		}

		try {
			// Transform onboarding data for API
			const structuredData = {
				businessName: onboardingData.businessName,
				phoneNumber: onboardingData.phoneNumber,
				addresses: onboardingData.addresses,
				businessHours: onboardingData.businessHours,
				pricing: onboardingData.pricing,
				faqs: onboardingData.faqs,
				additionalInfo: onboardingData.additionalInfo,
			};

			const workflows = {
				callWorkflows: onboardingData.callWorkflows || [],
				intakeWorkflows: onboardingData.intakeWorkflows || [],
				textingWorkflows: onboardingData.textingWorkflows || [],
			};

			await onboardingMutation.mutateAsync({
				agentId: selectedAgentId,
				structuredData,
				workflows,
			});

			// Success - close modal and clear localStorage
			notification.success({
				message: 'Setup Complete!',
				description: 'Your AI receptionist has been configured successfully.',
				duration: 3,
			});

			handleComplete();
		} catch (error) {
			// Error handling is done in the mutation hook
			console.error('Onboarding submission error:', error);
		}
	};

	const handlePrevious = () => {
		if (currentStage > 0) {
			setCurrentStage(currentStage - 1);
		}
	};

	const stages: OnboardingStage[] = [
		{
			id: 1,
			component: <WelcomeStage handleNext={() => setCurrentStage(currentStage + 1)} />,
		},
		{
			id: 2,
			component: (
				<BusinessInfoReviewStage
					onboardingData={onboardingData}
					updateOnboardingData={updateOnboardingData}
				/>
			),
		},
		{
			id: 3,
			component: (
				<CallTransferWorkflowStage
					onboardingData={onboardingData}
					updateOnboardingData={updateOnboardingData}
				/>
			),
			name: 'callWorkflows',
		},
		{
			id: 4,
			component: (
				<TextingWorkflowStage
					onboardingData={onboardingData}
					updateOnboardingData={updateOnboardingData}
				/>
			),
			name: 'textingWorkflows',
		},
		{
			id: 5,
			component: (
				<IntakeFormWorkflowStage
					onboardingData={onboardingData}
					updateOnboardingData={updateOnboardingData}
				/>
			),
			name: 'intakeWorkflows',
		},
		{
			id: 6,
			component: (
				<GoLiveStage
					handleSubmit={handleSubmitOnboarding}
					isLoading={onboardingMutation.isPending}
				/>
			),
		},
	];

	useEffect(() => {
		posthog.capture('started-onboarding-v5');
	}, []);

	return (
		<Modal
			open={visible}
			closable={isResellerDashboard}
			onCancel={() => {
				handleClose?.();
			}}
			maskClosable={false}
			footer={null}
			width={1000}
			style={{
				top: '5vh',
				maxHeight: '90vh',
			}}
			className="[&_.ant-modal-content]:rounded-2xl [&_.ant-modal-content]:overflow-hidden [&_.ant-modal-content]:p-0 [&_.ant-modal-body]:p-0 [&_.ant-modal-content]:max-h-[90vh] [&_.ant-modal-content]:flex [&_.ant-modal-content]:flex-col"
			styles={{
				mask: {
					backdropFilter: 'blur(8px)',
					backgroundColor: 'rgba(0, 0, 0, 0.5)',
				},
			}}
		>
			<div className="flex flex-col h-full max-h-[90vh]">
				{/* Scrollable content area */}
				<div className="flex-1 overflow-y-auto p-2 lg:p-8">
					<motion.div
						key={currentStage}
						initial={{ opacity: 0, x: 20 }}
						animate={{ opacity: 1, x: 0 }}
						exit={{ opacity: 0, x: -20 }}
						transition={{ duration: 0.3 }}
						className="h-full"
					>
						{stages[currentStage]?.component}
					</motion.div>
				</div>

				{/* Fixed footer with stage indicators and navigation */}
				<div className="p-6 border-t border-gray-200 bg-white">
					{/* Stage indicators */}
					<div className="flex justify-center gap-2 mb-4">
						{stages.map((_, index) => (
							<div
								key={index}
								className={`w-2 h-2 rounded-full transition-all duration-300 ${
									index === currentStage
										? 'bg-blue-500 scale-110'
										: index < currentStage
											? 'bg-green-500'
											: 'bg-gray-300'
								}`}
							/>
						))}
					</div>

					{/* Navigation */}
					<div className="mb-2 lg:mb-0 flex justify-between items-center gap-4">
						<Button
							onClick={handlePrevious}
							icon={<ArrowLeft className="w-4 h-4" />}
							className={`bg-transparent border border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-700 ${
								currentStage === 0 ? 'opacity-0 pointer-events-none' : ''
							}`}
						>
							Back
						</Button>
						<div className="flex gap-2">
							{!(currentStage === 0 || currentStage === stages.length - 1) && (
								<Button
									type="primary"
									onClick={handleNext}
									loading={onboardingMutation.isPending}
									iconPosition="end"
									icon={
										currentStage === stages.length - 1 ? (
											<Rocket className="w-4 h-4" />
										) : (
											<ArrowRight className="w-4 h-4" />
										)
									}
									className="flex items-center gap-2"
								>
									{getNextButtonText()}
								</Button>
							)}
						</div>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default OnboardingModal;
